import {
  AnimationFrameService,
  CellContextMenuEvent,
  Constants,
  GridApi,
  GridOptions,
  ICellRendererParams,
  ModelUpdatedEvent,
} from '@ag-grid-community/core'
import { PostProcessPopupParams } from '@ag-grid-community/core/dist/cjs/entities/gridOptions'
import { ChangeDetectorRef } from '@angular/core'
import {
  defaultNumberFilterOptions,
  defaultTextFilterOptions,
  FilterOptionDef,
} from '@northfork/ag-grid-angular'
import { heliosDefaultToolPanels } from './common-sidebar'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
} from './helios-column-types/default'
import { heliosColumnTypes } from './helios-column-types/helios-column-types'
import { generateMdiIcon } from './utils/icon-utils'

export const heliosSizingAdjustments = {
  /**
   * For some reason the default is 32, which makes it off centered
   * Centering the text on the row and allowing for a little more data density.
   */
  rowHeight: 25,
  // Shrinks the header row so it's more comfortable sized
  headerHeight: 28,
}

/**
 * Disable change detection on the grid while scrolling.
 * Without this, angular will fire basically every pixel of the grid while scrolling.
 *
 * Needs to be a function because it needs access
 * to the component's change detector
 *
 * Usage:
 * ```typescript
 *
 * constructor(private changeDetectorRef: ChangeDetectorRef) {}
 *
 * const gridOptions: GridOptions = {
 *  ...
 *  ...disableChangeDetectionOnScroll(this.changeDetector)
 * }
 * ```
 */
export function disableChangeDetectionOnScroll(
  changeDetectorRef: ChangeDetectorRef,
) {
  return {
    onBodyScroll: () => changeDetectorRef.detach(),
    onBodyScrollEnd: () => changeDetectorRef.reattach(),
  }
}

/** Ag-grid cell renderer that displays a checkbox icon.
 *
 * @returns When params.value is null, returns empty string. Otherwise, returns the checkbox icon.
 */
export const checkboxNullableCellRenderer = (params: ICellRendererParams) => {
  const checked: boolean | undefined = params.value
  if (checked == undefined) {
    return ''
  }
  const icon = checked ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'

  return generateMdiIcon(icon)
}

/**
 * @internal
 * This extends the _default_ Northfork Grid options which are then _merged_ with the grid options created directly by a grid instance.
 * Any conflicts will prefer the options from the grid instance.
 * This is not called directly from Helios code, but instead by the HeliosAgGridConfigModule (ag-grid module "plugin", not angular module).
 *
 * Code Links
 *   default NF Grid Options:     https://stash.arbfund.com/projects/NFORK/repos/northfork/browse/packages/ag-grid/src/defaults/NfGridOptions.ts#146
 *   extend default:              https://stash.arbfund.com/projects/NFORK/repos/northfork/browse/packages/ag-grid-angular/src/grid-options/grid-option.service.ts#26
 *   merge instance over default: https://stash.arbfund.com/projects/NFORK/repos/northfork/browse/packages/ag-grid-angular/src/ag-grid/ag-grid.component.ts#249
 */
export function extendDefaultGridOptions(
  gridOptions: GridOptions,
): GridOptions {
  return {
    ...gridOptions,
    columnTypes: {
      ...heliosColumnTypes,
      ...gridOptions.columnTypes,
    },
    defaultColDef: {
      ...heliosDefaultColDef,
      ...gridOptions.defaultColDef,
    },
    sideBar: {
      toolPanels: [...heliosDefaultToolPanels],
    },
    allowContextMenuWithControlKey: true,
    enableRangeSelection: true,
    skipHeaderOnAutoSize: true,
    suppressAnimationFrame: true,
    suppressMultiRangeSelection: true,
    suppressContextMenu: false,
    allowDragFromColumnsToolPanel: true,
    overlayNoRowsTemplate: 'No data',
    suppressNoRowsOverlay: false,
    suppressCsvExport: true,
    defaultCsvExportParams: {
      processCellCallback: params => {
        const value = params.value
        if (
          value &&
          typeof value === 'string' &&
          ['=', '+', '-', '@'].some(char => value.startsWith(char))
        ) {
          return `'${value}`
        }
        return value
      },
    },
    onCellContextMenu: (event: CellContextMenuEvent) => {
      //right-click should select the node if not already selected - and clear any existing selection
      if (event.node.isSelected()) return

      event.api.deselectAll()
      event.node.setSelected(true)
    },
  }
}

/**
 * show/hide no data message in tables
 */
function onModelUpdated(event: ModelUpdatedEvent) {
  if (event.api.getDisplayedRowCount() > 0) {
    event.api.hideOverlay()
  } else {
    event.api.showNoRowsOverlay()
  }
}

interface IAnimationFrameService {
  verifyAnimationFrameOn: () => void
}

/**
 * Ag Grid console.logs a warning hundreds or thousands of times.
 * This just mutes those console.logs.
 */
;(
  AnimationFrameService.prototype as unknown as IAnimationFrameService
).verifyAnimationFrameOn = () => {
  // Do nothing
}

/**
 * Ag Grid console.logs a warning many many times. This just mutes those console.logs.
 *
 * Northfork requests this every time a grid is created and this is just unnecessary.
 *
 * NFORK-3048
 */
const oldGetToolPanelInstance = GridApi.prototype.getToolPanelInstance
GridApi.prototype.getToolPanelInstance = function (name: string) {
  if (name === 'configSaver') {
    return undefined
  }

  return oldGetToolPanelInstance.call(this, name)
}

/** Default Helios server side row model configuration. */
export const heliosServerSideRowConfig: GridOptions = {
  ...heliosSizingAdjustments,
  rowModelType: Constants.ROW_MODEL_TYPE_SERVER_SIDE,
  // Memory increases if this number is 50. From 50 down to 15, cuts memory usage and listeners in half
  rowBuffer: 15,
  postProcessPopup,
  cacheBlockSize: 50,
  maxBlocksInCache: 3,
  popupParent: document.body,
  animateRows: false,
  valueCache: true,
  serverSideStoreType: 'partial',
  blockLoadDebounceMillis: 50,
  defaultColDef: {
    filterParams: {
      debounceMs: 0,
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      defaultToNothingSelected: true,
    },
    width: heliosDefaultColWidth.Default,
    tooltipComponentParams: {
      showTooltip: () => true,
    },
  },
  overlayNoRowsTemplate: 'No data',
  suppressNoRowsOverlay: false,
  onModelUpdated,
  enableBrowserTooltips: true,
  columnTypes: { ...heliosColumnTypes },
}

/** Default Helios client side row model configuration. */
export const heliosClientSideRowConfig: GridOptions = {
  ...(heliosSizingAdjustments as GridOptions),
  rowModelType: Constants.ROW_MODEL_TYPE_CLIENT_SIDE,
  popupParent: document.body,
  postProcessPopup,
  animateRows: false,
  valueCache: true,
  defaultColDef: {
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      defaultToNothingSelected: true,
    },
    width: heliosDefaultColWidth.Default,
    sortable: true,
    filter: true,
    enableRowGroup: true,
    tooltipComponentParams: {
      showTooltip: () => true,
    },
  },
  enableBrowserTooltips: true,
  columnTypes: { ...heliosColumnTypes },
  enableRangeSelection: true,
  suppressCopyRowsToClipboard: true,
}

export const TextFilterOptionsWithContainsIn: (
  | string
  | FilterOptionDef<string>
)[] = [
  ...(defaultTextFilterOptions.filterOptions as string[]),
  {
    displayKey: 'containsIn',
    displayName: 'Contains In',
    test: () => false, //NOOP server side
  } as FilterOptionDef<string>,
  {
    displayKey: 'notContains',
    displayName: 'Not Contains',
    test: () => false, //NOOP server side
  } as FilterOptionDef<string>,
]

export const NumberFilterOptionsWithoutEquals: (
  | string
  | FilterOptionDef<string>
)[] = [
  ...makeInDefaultAndRemoveEquals(
    defaultNumberFilterOptions.filterOptions as string[],
  ),
  {
    displayKey: 'equalsAbsolute',
    displayName: 'Equals Absolute',
    test: () => false, //NOOP server side
  } as FilterOptionDef<string>,
]

export const NumberFilterOptionsWithAbsolute: (
  | string
  | FilterOptionDef<string>
)[] = [
  ...(defaultNumberFilterOptions.filterOptions as string[]),
  {
    displayKey: 'equalsAbsolute',
    displayName: 'Equals Absolute',
    test: () => false, //NOOP server side
  } as FilterOptionDef<string>,
]

/**
 * This method will move the context menu/popup upwards if it will get cutoff by the bottom of the page.
 *
 * @param params
 */
function postProcessPopup(params: PostProcessPopupParams) {
  // window.innerHeight, not document.body.offsetHeight: the popup measures in
  // viewport px under the UI scale while body reports its own pre-zoom height, and
  // mixing them inflates the bound until this guard silently never fires.
  const bodyMaxTop = window.innerHeight - 100 - params.ePopup.offsetHeight
  if (params.ePopup.offsetTop > bodyMaxTop) {
    params.ePopup.style.top = `${bodyMaxTop}px`
  }
}

/**
 * This method will remove equals option from number filter and make IN default filter.
 *
 * @param defaultFilters
 */
function makeInDefaultAndRemoveEquals(defaultFilters: string[]) {
  const index = defaultFilters.indexOf('in')
  if (index !== -1) {
    defaultFilters.splice(index, 1)
    defaultFilters.unshift('in')
  }

  return defaultFilters.filter(key => key != 'equals')
}
